import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SnackBar from "../../components/SnackBar";
import { buscarComboPorCodigo } from "../../services/auth.service";
import { validarNovaApuracao as validarNovaApuracaoApi } from "../../services/api-relatorios.service";
import { criarApuracao } from "../../services/apuracao.service";
import { isRelatorioApuracaoProcessavel } from "../../utils/relatorioApuracao.util";
import TiposApuracaoAutocomplete from "./components/TiposApuracaoAutocomplete";
import TermosExclusaoField from "./components/TermosExclusaoField";
import {
  adaptarPeriodoParaGranularidade,
  configRelatorioApuracao,
  GRANULARIDADE_PERIODO,
} from "./configRelatorioApuracao.util";
import styles from "./styles.module.css";
import {
  dateFromPeriodoISO,
  fmtMesAno,
  gerarAnosSelect,
  isInicioMaisDe5AnosAtras,
  isOrdemPeriodoValida,
  MSG_COMPETENCIA_FUTURA,
  normCnpj,
  periodoPadraoAposFim,
  periodoPadraoAtual,
  periodoTemCompetenciaFutura,
} from "./utils";
import { formatValue } from "../../utils/formatValue";
import {
  bloqueiaCriacaoFromValidacao,
  mensagensErroFromValidacao,
  mensagensInfoFromValidacao,
} from "./validarNovaApuracao.util";
import {
  carregarComboItens,
  cnpjDaEmpresaCombo,
  resolverGrupoEmpresaPorCnpjDireto,
} from "./resolverGrupoEmpresa.util";

const CNPJ_DIGITOS = /^\d{14}$/;
const VALIDACAO_DEBOUNCE_MS = 400;
/** TXM-325 item 05 — período na UI SPED/V3 é só ano (contribuições anuais). */
const ANOS = gerarAnosSelect();

const MSG_APENAS_COMPETENCIAS_NOVAS =
  "Apenas competências que ainda não fazem parte desta apuração serão geradas.";

function anoAtualNum() {
  return new Date().getFullYear();
}

function periodoDefaultAno() {
  const y = String(anoAtualNum());
  return { ini: y, fim: y };
}

const ModalNovaApuracao = ({
  open,
  onClose,
  onSuccess,
  onError,
  onClearFeedback,
  tiposApuracao = [],
  loadingTiposApuracao = false,
  rowContext = null,
}) => {
  const modoAdicionar = Boolean(rowContext);
  const [grupos, setGrupos] = useState([]);
  const [grupoSelecionado, setGrupoSelecionado] = useState(null);
  const [listaEmpresas, setListaEmpresas] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [loading, setLoading] = useState(false);
  const [idRelatorio, setIdRelatorio] = useState("");
  const [periodoInicial, setPeriodoInicial] = useState(
    () => periodoDefaultAno().ini
  );
  const [periodoFinal, setPeriodoFinal] = useState(() => periodoDefaultAno().fim);
  const [termos, setTermos] = useState([]);
  // TXM-325 — 95% dos casos são só contas de resultado; default "sim". Só se aplica na
  // criação: "Adicionar período" mantém a regra já definida na apuração existente.
  const [contasCriterio, setContasCriterio] = useState("resultado");
  /** TXM-418 — CAT-42: Tabela Finalidade de Entrega do Arquivo (Portaria CAT-42/2018,
   * leiaute oficial v1.1.0) — únicos 3 códigos existentes no manual. "00" é o default
   * correto pra remessa regular (a grande maioria dos casos). */
  const [codFinCat42, setCodFinCat42] = useState("00");
  const [gerarArquivoDigitalCat42, setGerarArquivoDigitalCat42] = useState(false);
  const [validacaoApi, setValidacaoApi] = useState(null);
  const [loadingValidacao, setLoadingValidacao] = useState(false);
  const [validacaoIndisponivel, setValidacaoIndisponivel] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const codigoPeriodoRef = useRef(null);

  const cnpjEmpresaSelecionada = modoAdicionar
    ? normCnpj(rowContext?.cnpj)
    : cnpjDaEmpresaCombo(empresaSelecionada);

  const nomeEmpresa =
    empresaSelecionada?.detalhes ?? rowContext?.empresa ?? "";

  const relatorioSelecionado = modoAdicionar
    ? tiposApuracao.find(
        (r) => String(r.codigoRelatorio) === String(rowContext?.tipo ?? "")
      )
    : tiposApuracao.find((r) => String(r.idRelatorio) === String(idRelatorio));

  const codigoRelatorio =
    relatorioSelecionado?.codigoRelatorio ?? rowContext?.tipo ?? "";
  const config = configRelatorioApuracao(codigoRelatorio);
  const periodoPorMes =
    config.granularidadePeriodo === GRANULARIDADE_PERIODO.MES;

  const periodoIni = periodoInicial;
  const periodoFim = periodoFinal;
  const periodoPreenchido = Boolean(periodoInicial && periodoFinal);
  const ordemPeriodoLocal = !periodoPreenchido
    ? true
    : periodoPorMes
      ? isOrdemPeriodoValida(periodoInicial, periodoFinal)
      : Number(periodoInicial) <= Number(periodoFinal);
  const ordemPeriodoValida =
    validacaoApi?.periodo != null
      ? validacaoApi.periodo.ordemValida
      : ordemPeriodoLocal;
  const anoCorrente = anoAtualNum();
  const competenciaFuturaLocal =
    periodoPreenchido &&
    ordemPeriodoLocal &&
    (periodoPorMes
      ? periodoTemCompetenciaFutura(periodoInicial, periodoFinal)
      : Number(periodoInicial) > anoCorrente ||
        Number(periodoFinal) > anoCorrente);
  const competenciaFutura =
    validacaoApi?.periodo != null
      ? validacaoApi.periodo.competenciaFutura === true
      : competenciaFuturaLocal;
  const relatorioProcessavel = relatorioSelecionado
    ? isRelatorioApuracaoProcessavel(
        relatorioSelecionado.nome,
        relatorioSelecionado.codigoRelatorio
      )
    : false;
  const alertaPeriodoMais5Anos =
    validacaoApi?.periodo != null
      ? validacaoApi.periodo.exibirAlerta5Anos
      : periodoPreenchido &&
        ordemPeriodoLocal &&
        !competenciaFuturaLocal &&
        config.alerta5Anos &&
        relatorioProcessavel &&
        (periodoPorMes
          ? isInicioMaisDe5AnosAtras(periodoInicial)
          : anoCorrente - Number(periodoInicial) > 5);
  /**
   * TXM-325 item 05 — o campo só é exibido na criação; em "Adicionar período" fica oculto e
   * `termos` é zerado (efeito abaixo). A borda NÃO ignora `termos_exclusao` no modo atualizar —
   * ela aplica o valor recebido normalmente. `termos_exclusao` afeta só o filtro de sumarização
   * desta execução; enviar `[]` significa "sem filtro de exclusão neste ciclo", não apaga dado
   * nenhum. Por isso `handleConfirmar` envia `termos_exclusao` sempre (mesmo vazio) e sem
   * condicional — ver comentário lá. NÃO reintroduzir a ideia de que a borda ignora este campo.
   *
   * TXM-336 / config por código — termos e contas ECD só quando a config do relatório permite.
   */
  const exibeTermosExclusao =
    config.campos.termosExclusao && relatorioProcessavel && !modoAdicionar;
  const exibeContasCriterio = config.campos.contasCriterio && !modoAdicionar;
  /** TXM-418 — ICMS é a apuração-base; ao criá-la, a filha CAT-42 aparece como parâmetro
   * dela (a filha Notas Fiscais, TXM-597, não tem parâmetros próprios ainda). */
  const exibeParametrosIcms = codigoRelatorio === "ICMS_APURACAO";
  const conflitoBloqueia = bloqueiaCriacaoFromValidacao(validacaoApi);
  const mensagensInfo = mensagensInfoFromValidacao(validacaoApi);
  const mensagensErro = mensagensErroFromValidacao(validacaoApi);
  const podeValidarRemoto =
    CNPJ_DIGITOS.test(cnpjEmpresaSelecionada) &&
    relatorioSelecionado &&
    periodoPreenchido;

  const identidadePreenchida = modoAdicionar
    ? CNPJ_DIGITOS.test(cnpjEmpresaSelecionada) && Boolean(relatorioSelecionado)
    : grupoSelecionado &&
      empresaSelecionada &&
      CNPJ_DIGITOS.test(cnpjEmpresaSelecionada) &&
      Boolean(relatorioSelecionado);

  // No modo "adicionar período", grupo/empresa já vêm fixos da linha selecionada
  // (identidadePreenchida nem depende deles) — não faz sentido o botão ficar
  // bloqueado esperando a resolução de grupo econômico em background, que pode
  // demorar mais que a validação de período (efeito colateral: a mensagem "período
  // será ampliado" aparece, mas o clique no botão não faz nada até essa resolução
  // terminar).
  const formValido =
    identidadePreenchida &&
    periodoPreenchido &&
    ordemPeriodoValida &&
    !competenciaFutura &&
    !conflitoBloqueia &&
    (modoAdicionar || (!loadingGrupos && !loadingEmpresas)) &&
    !loadingTiposApuracao &&
    !(podeValidarRemoto && loadingValidacao);

  const limpar = useCallback(() => {
    setGrupoSelecionado(null);
    setListaEmpresas([]);
    setEmpresaSelecionada(null);
    setIdRelatorio("");
    const atual = periodoDefaultAno();
    setPeriodoInicial(atual.ini);
    setPeriodoFinal(atual.fim);
    setTermos([]);
    setContasCriterio("resultado");
    setValidacaoApi(null);
    setLoadingValidacao(false);
    setValidacaoIndisponivel(false);
    setObjSnackBar({ type: "", msg: "" });
    codigoPeriodoRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) {
      limpar();
      return;
    }

    if (modoAdicionar && rowContext) {
      const cfg = configRelatorioApuracao(rowContext.tipo);
      if (cfg.granularidadePeriodo === GRANULARIDADE_PERIODO.MES) {
        const prox = periodoPadraoAposFim(rowContext.periodoFim);
        setPeriodoInicial(prox.ini);
        setPeriodoFinal(prox.fim);
      } else {
        const dFim = dateFromPeriodoISO(rowContext.periodoFim);
        const ano = String(dFim ? dFim.getFullYear() + 1 : anoAtualNum());
        setPeriodoInicial(ano);
        setPeriodoFinal(ano);
      }
      codigoPeriodoRef.current = String(rowContext.tipo ?? "").trim().toUpperCase();
      const cnpj = normCnpj(rowContext.cnpj);
      setEmpresaSelecionada({ id: cnpj, detalhes: rowContext.empresa });
      const tipo = tiposApuracao.find(
        (t) => String(t.codigoRelatorio) === String(rowContext.tipo ?? "")
      );
      if (tipo) setIdRelatorio(String(tipo.idRelatorio));
    } else {
      const atual = periodoDefaultAno();
      setPeriodoInicial(atual.ini);
      setPeriodoFinal(atual.fim);
      codigoPeriodoRef.current = null;
    }
  }, [open, modoAdicionar, rowContext, tiposApuracao, limpar]);

  // Troca de tipo na criação: adapta ano ↔ mês conforme a config do relatório.
  useEffect(() => {
    if (!open || modoAdicionar || !codigoRelatorio) return;
    const codigoNorm = String(codigoRelatorio).trim().toUpperCase();
    const prev = codigoPeriodoRef.current;
    if (prev === codigoNorm) return;

    const cfg = configRelatorioApuracao(codigoNorm);
    const aindaDefaultAno =
      /^\d{4}$/.test(periodoInicial) &&
      periodoInicial === periodoFinal &&
      Number(periodoInicial) === anoAtualNum();

    if (cfg.granularidadePeriodo === GRANULARIDADE_PERIODO.MES && aindaDefaultAno) {
      // Default anual (ano corrente) → competência do mês atual (melhor UX no eSocial).
      const padrao = periodoPadraoAtual();
      setPeriodoInicial(padrao.ini);
      setPeriodoFinal(padrao.fim);
    } else {
      const adapted = adaptarPeriodoParaGranularidade(
        periodoInicial,
        periodoFinal,
        cfg.granularidadePeriodo
      );
      setPeriodoInicial(adapted.ini);
      setPeriodoFinal(adapted.fim);
    }
    codigoPeriodoRef.current = codigoNorm;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só reage à troca de código; período lido no momento da troca
  }, [open, modoAdicionar, codigoRelatorio]);

  useEffect(() => {
    if (!exibeTermosExclusao) setTermos([]);
  }, [exibeTermosExclusao]);

  useEffect(() => {
    if (!exibeParametrosIcms) {
      setCodFinCat42("00");
      setGerarArquivoDigitalCat42(false);
    }
  }, [exibeParametrosIcms]);

  useEffect(() => {
    if (!open || tiposApuracao.length === 0 || modoAdicionar) return;
    setIdRelatorio((current) => {
      const valido =
        current &&
        tiposApuracao.some((t) => String(t.idRelatorio) === String(current));
      return valido ? current : String(tiposApuracao[0].idRelatorio);
    });
  }, [open, tiposApuracao, modoAdicionar]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;

    if (modoAdicionar && rowContext) {
      const cnpj = normCnpj(rowContext.cnpj);
      if (!CNPJ_DIGITOS.test(cnpj)) return undefined;

      setLoadingGrupos(true);
      setLoadingEmpresas(true);
      (async () => {
        try {
          const resolvido = await resolverGrupoEmpresaPorCnpjDireto(
            cnpj,
            buscarComboPorCodigo
          );
          if (cancelled) return;

          if (resolvido) {
            setGrupos([resolvido.grupo]);
            setGrupoSelecionado(resolvido.grupo);
            setListaEmpresas(resolvido.empresas);
            setEmpresaSelecionada(resolvido.empresa);
          }
        } catch {
          if (!cancelled) {
            setGrupos([]);
            setObjSnackBar({
              type: "error",
              msg: "Não foi possível carregar os dados da empresa.",
            });
          }
        } finally {
          if (!cancelled) {
            setLoadingGrupos(false);
            setLoadingEmpresas(false);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    setLoadingGrupos(true);
    (async () => {
      try {
        const lista = await carregarComboItens(
          "GRUPOECONOMICO",
          buscarComboPorCodigo
        );
        if (!cancelled) setGrupos(lista);
      } catch {
        if (!cancelled) {
          setGrupos([]);
          setObjSnackBar({
            type: "error",
            msg: "Não foi possível carregar os grupos econômicos.",
          });
        }
      } finally {
        if (!cancelled) setLoadingGrupos(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, modoAdicionar, rowContext]);

  useEffect(() => {
    if (modoAdicionar || !grupoSelecionado) {
      if (!modoAdicionar && !grupoSelecionado) {
        setListaEmpresas([]);
        setEmpresaSelecionada(null);
      }
      return undefined;
    }

    let cancelled = false;
    setLoadingEmpresas(true);
    (async () => {
      try {
        const lista = await carregarComboItens(
          `EMPRESA_POR_GRUPO_${grupoSelecionado.id}`,
          buscarComboPorCodigo
        );
        if (!cancelled) {
          setListaEmpresas(lista);
          setEmpresaSelecionada(null);
        }
      } catch {
        if (!cancelled) {
          setListaEmpresas([]);
          setEmpresaSelecionada(null);
          setObjSnackBar({
            type: "error",
            msg: "Não foi possível carregar as empresas do grupo selecionado.",
          });
        }
      } finally {
        if (!cancelled) setLoadingEmpresas(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [grupoSelecionado, modoAdicionar]);

  useEffect(() => {
    if (!open) return undefined;
    // Um toast de erro de uma tentativa anterior (ex.: período já coberto)
    // não deve continuar na tela depois que o usuário muda ano/grupo/empresa/
    // tipo — o feedback tem que refletir os campos atuais, não uma submissão
    // antiga que já não corresponde ao que está preenchido agora.
    onClearFeedback?.();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cnpjEmpresaSelecionada, relatorioSelecionado, periodoIni, periodoFim]);

  useEffect(() => {
    if (!open || !podeValidarRemoto) {
      setValidacaoApi(null);
      setLoadingValidacao(false);
      setValidacaoIndisponivel(false);
      return undefined;
    }

    // Síncrono, fora do setTimeout: fecha a janela em que o botão ficava clicável com o
    // resultado de validação da combinação ANTERIOR enquanto o debounce ainda não disparou
    // a revalidação da combinação nova (formValido já considera loadingValidacao).
    setLoadingValidacao(true);
    setValidacaoIndisponivel(false);

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await validarNovaApuracaoApi({
          cnpj: cnpjEmpresaSelecionada,
          idRelatorio: relatorioSelecionado.idRelatorio,
          codigo_relatorio: relatorioSelecionado.codigoRelatorio,
          periodo_inicio: periodoIni,
          periodo_fim: periodoFim,
        });
        if (!cancelled) {
          setValidacaoApi(res?.sucesso ? res : null);
          setValidacaoIndisponivel(false);
        }
      } catch {
        if (!cancelled) {
          setValidacaoApi(null);
          setValidacaoIndisponivel(true);
        }
      } finally {
        if (!cancelled) setLoadingValidacao(false);
      }
    }, VALIDACAO_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    open,
    podeValidarRemoto,
    cnpjEmpresaSelecionada,
    relatorioSelecionado,
    periodoIni,
    periodoFim,
  ]);

  const handleClose = () => {
    limpar();
    onClose();
  };

  const handleConfirmar = async () => {
    if (!modoAdicionar && !grupoSelecionado) {
      setObjSnackBar({ type: "error", msg: "Selecione o grupo econômico." });
      return;
    }
    if (!modoAdicionar && !empresaSelecionada) {
      setObjSnackBar({
        type: "error",
        msg:
          listaEmpresas.length === 0
            ? "Não há empresas disponíveis para o grupo selecionado."
            : "Selecione uma empresa.",
      });
      return;
    }
    if (!CNPJ_DIGITOS.test(cnpjEmpresaSelecionada)) {
      setObjSnackBar({
        type: "error",
        msg: "CNPJ da empresa inválido (esperado 14 dígitos).",
      });
      return;
    }
    if (!periodoPreenchido) {
      setObjSnackBar({
        type: "error",
        msg: config.msgPeriodoObrigatorio,
      });
      return;
    }
    if (!ordemPeriodoValida) {
      setObjSnackBar({
        type: "error",
        msg: config.msgOrdemPeriodo,
      });
      return;
    }
    if (competenciaFutura) {
      setObjSnackBar({
        type: "error",
        msg: MSG_COMPETENCIA_FUTURA,
      });
      return;
    }
    if (!relatorioSelecionado) {
      setObjSnackBar({
        type: "error",
        msg: "Selecione uma apuração.",
      });
      return;
    }

    setLoading(true);
    try {
      const res = await criarApuracao({
        empresa: nomeEmpresa,
        cnpj: cnpjEmpresaSelecionada,
        periodo_inicio: periodoIni,
        periodo_fim: periodoFim,
        // Sempre enviado (mesmo [] em "Adicionar período") — a borda grava no agregado só na criação;
        // no atualizar o bloco sumarização não é escrito (herda do agregado).
        sumarizacao: { termos_exclusao: termos },
        // Só na criação de SPED/V3; "Adicionar período" mantém a regra já definida,
        // e eSocial não tem esse conceito (contas contábeis ECD).
        ...(exibeContasCriterio
          ? {
              apuracao: {
                contas_criterio: contasCriterio,
              },
            }
          : {}),
        configuracoes: {
          id_relatorio: relatorioSelecionado.idRelatorio,
          ...(exibeParametrosIcms
            ? { gerar_arquivo_digital_cat42: gerarArquivoDigitalCat42 }
            : {}),
          // TXM-418 — `codFinCat42` (select "Finalidade do arquivo") NÃO vai no payload de
          // criação de propósito: `configuracoes` só aceita boolean (validação compartilhada
          // com /relatorios/processar, ver processarRelatorio.validator.ts) e, mesmo que
          // aceitasse string, o backend ainda não lê esse valor em lugar nenhum — o export do
          // arquivo digital chama gerar_arquivo_digital_icmsstcat42 com '00' fixo. O campo
          // fica só informativo (com os 3 códigos reais do manual CAT-42) até alguém decidir
          // investir em ligar de ponta a ponta.
        },
      });
      if (res?.sucesso) {
        onSuccess?.(
          modoAdicionar
            ? `Período adicionado à apuração de ${nomeEmpresa}.`
            : `Apuração criada para ${nomeEmpresa} (${relatorioSelecionado.codigoRelatorio}).`
        );
        handleClose();
      } else {
        onError?.(
          res?.mensagem ||
            (modoAdicionar
              ? "Não foi possível adicionar o período."
              : "Não foi possível criar a apuração.")
        );
      }
    } catch (e) {
      onError?.(
        e?.message ||
          (modoAdicionar ? "Erro ao adicionar período." : "Erro ao criar apuração.")
      );
    } finally {
      setLoading(false);
    }
  };

  const tituloModal = modoAdicionar ? "Adicionar período à apuração" : "Nova apuração";
  const aguardandoResolucaoGrupoEmpresa =
    !modoAdicionar && (loadingGrupos || loadingEmpresas);
  const labelConfirmar = loading
    ? modoAdicionar
      ? "Adicionando…"
      : "Criando…"
    : aguardandoResolucaoGrupoEmpresa
      ? "Carregando…"
      : modoAdicionar
        ? "Adicionar período"
        : "Criar apuração";

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <div className={styles.title}>
            <AddIcon sx={{ marginRight: ".5rem" }} />
            {tituloModal}
          </div>
        </DialogTitle>
        <DialogContent className={styles.content}>
          <div className={styles.field}>
            <Autocomplete
              options={grupos}
              value={grupoSelecionado}
              onChange={(_, value) => {
                if (modoAdicionar) return;
                setGrupoSelecionado(value);
                setObjSnackBar({ type: "", msg: "" });
              }}
              getOptionLabel={(o) => o?.detalhes ?? ""}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              fullWidth
              size="small"
              loading={loadingGrupos}
              disabled={loading || modoAdicionar}
              readOnly={modoAdicionar}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Grupo econômico"
                  placeholder={
                    modoAdicionar && !grupoSelecionado && !loadingGrupos
                      ? "Grupo não identificado"
                      : undefined
                  }
                />
              )}
            />
          </div>

          <div className={styles.field}>
            <Autocomplete
              options={listaEmpresas.length ? listaEmpresas : empresaSelecionada ? [empresaSelecionada] : []}
              value={empresaSelecionada}
              onChange={(_, value) => {
                if (modoAdicionar) return;
                setEmpresaSelecionada(value);
                setObjSnackBar({ type: "", msg: "" });
              }}
              getOptionLabel={(o) => o?.detalhes ?? ""}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              fullWidth
              size="small"
              loading={loadingEmpresas}
              disabled={loading || modoAdicionar || (!grupoSelecionado && !modoAdicionar)}
              readOnly={modoAdicionar}
              noOptionsText={
                !grupoSelecionado && !modoAdicionar
                  ? "Selecione um grupo econômico primeiro."
                  : loadingEmpresas
                    ? "Carregando empresas…"
                    : "Nenhuma empresa disponível para o grupo selecionado."
              }
              renderOption={(props, option) => {
                const cnpj = cnpjDaEmpresaCombo(option);
                const cnpjValido = CNPJ_DIGITOS.test(cnpj);
                // O combo EMPRESA_POR_GRUPO_ manda o próprio CNPJ em `detalhes` (não a razão
                // social) — sem esta checagem a opção mostrava o mesmo CNPJ duas vezes (cru em
                // cima, formatado embaixo). Se um dia `detalhes` vier com um nome, volta a
                // mostrar nome + CNPJ.
                const detalhesEhOProprioCnpj =
                  String(option.detalhes ?? "").replace(/\D/g, "") === cnpj;
                return (
                  <li {...props} key={option.id}>
                    <div>
                      <Typography variant="body2">
                        {cnpjValido && detalhesEhOProprioCnpj
                          ? formatValue("cnpj", cnpj)
                          : option.detalhes}
                      </Typography>
                      {cnpjValido && !detalhesEhOProprioCnpj && (
                        <Typography variant="caption" color="text.secondary">
                          {formatValue("cnpj", cnpj)}
                        </Typography>
                      )}
                    </div>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Empresa"
                  placeholder={
                    modoAdicionar
                      ? undefined
                      : !grupoSelecionado
                        ? "Selecione um grupo econômico primeiro"
                        : loadingEmpresas
                          ? "Carregando empresas..."
                          : undefined
                  }
                />
              )}
            />
          </div>

          <div className={styles.field}>
            <TiposApuracaoAutocomplete
              options={tiposApuracao}
              value={relatorioSelecionado ?? null}
              onChange={(_, option) => {
                if (modoAdicionar) return;
                setIdRelatorio(option ? String(option.idRelatorio) : "");
                setObjSnackBar({ type: "", msg: "" });
              }}
              loading={loadingTiposApuracao}
              disabled={loading || modoAdicionar}
              error={!loadingTiposApuracao && tiposApuracao.length === 0}
              helperText={
                !loadingTiposApuracao && tiposApuracao.length === 0
                  ? "Nenhum relatório de apuração disponível (apuracao=true)."
                  : undefined
              }
            />
          </div>

          {modoAdicionar && rowContext && (
            <>
              <div className={styles.readonlyBlock}>
                <span className={styles.readonlyLabel}>Período atual da apuração</span>
                <Typography className={styles.readonlyValue}>
                  {fmtMesAno(rowContext.periodoInicio)} –{" "}
                  {fmtMesAno(rowContext.periodoFim)}
                </Typography>
              </div>

              <Alert severity="info" sx={{ width: "100%" }}>
                {MSG_APENAS_COMPETENCIAS_NOVAS}
              </Alert>
            </>
          )}

          <div className={styles.periodRow}>
            {periodoPorMes ? (
              <>
                <TextField
                  className={styles.periodField}
                  size="small"
                  type="month"
                  label={config.labelPeriodoInicial}
                  value={periodoInicial}
                  onChange={(e) => setPeriodoInicial(e.target.value)}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ "aria-label": config.labelPeriodoInicial }}
                />
                <TextField
                  className={styles.periodField}
                  size="small"
                  type="month"
                  label={config.labelPeriodoFinal}
                  value={periodoFinal}
                  onChange={(e) => setPeriodoFinal(e.target.value)}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ "aria-label": config.labelPeriodoFinal }}
                />
              </>
            ) : (
              <>
                <FormControl size="small" className={styles.periodField} disabled={loading}>
                  <InputLabel id="ano-inicial-label">{config.labelPeriodoInicial}</InputLabel>
                  <Select
                    labelId="ano-inicial-label"
                    label={config.labelPeriodoInicial}
                    value={periodoInicial}
                    onChange={(e) => setPeriodoInicial(e.target.value)}
                  >
                    {ANOS.map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" className={styles.periodField} disabled={loading}>
                  <InputLabel id="ano-final-label">{config.labelPeriodoFinal}</InputLabel>
                  <Select
                    labelId="ano-final-label"
                    label={config.labelPeriodoFinal}
                    value={periodoFinal}
                    onChange={(e) => setPeriodoFinal(e.target.value)}
                  >
                    {ANOS.map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {exibeContasCriterio && (
              <div className={styles.contasResultadoField}>
                <span className={styles.contasResultadoLabel}>Apenas contas de resultado</span>
                <Switch
                  checked={contasCriterio === "resultado"}
                  onChange={(e) =>
                    setContasCriterio(e.target.checked ? "resultado" : "todas")
                  }
                  disabled={loading}
                  size="small"
                  inputProps={{ "aria-label": "Apenas contas de resultado" }}
                />
              </div>
            )}
          </div>

          {exibeParametrosIcms && (
            <>
              <div className={styles.field}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Parâmetros do ICMS-ST
                </Typography>
                <Alert severity="info">
                  Apuração do ICMS-ST ainda não disponível — os parâmetros específicos
                  serão adicionados em uma fase futura.
                </Alert>
              </div>

              <div className={styles.field}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Parâmetros do ICMS-ST CAT-42
                </Typography>
                <FormControl size="small" fullWidth sx={{ mb: 1.5 }} disabled={loading}>
                  <InputLabel id="cod-fin-cat42-label">Finalidade do arquivo</InputLabel>
                  <Select
                    labelId="cod-fin-cat42-label"
                    label="Finalidade do arquivo"
                    value={codFinCat42}
                    onChange={(e) => setCodFinCat42(e.target.value)}
                  >
                    {/* Tabela Finalidade de Entrega do Arquivo — Leiaute Arquivo Digital
                        ICMS-ST v1.1.0 (Portaria CAT-42/2018), únicos 3 códigos existentes. */}
                    <MenuItem value="00">00 — Remessa regular de arquivo</MenuItem>
                    <MenuItem value="01">01 — Remessa requerida por intimação específica</MenuItem>
                    <MenuItem value="02">02 — Substituição de arquivo remetido anteriormente</MenuItem>
                  </Select>
                </FormControl>
                <div className={styles.readonlyBlock}>
                  <span className={styles.readonlyLabel}>Origem do saldo inicial</span>
                  <Typography className={styles.readonlyValue}>
                    Período anterior no sistema
                  </Typography>
                </div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={gerarArquivoDigitalCat42}
                      onChange={(e) => setGerarArquivoDigitalCat42(e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label="Gerar arquivo digital"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  A apuração CAT-42 vinculada é criada automaticamente para o mesmo
                  CNPJ e período. A geração do arquivo digital hoje precisa ser
                  solicitada manualmente na tela de detalhe do CAT-42; esta opção só
                  registra a preferência para a automação futura.
                </Typography>
              </div>
            </>
          )}

          {exibeTermosExclusao && (
            <div className={styles.field}>
              <TermosExclusaoField
                termos={termos}
                onChange={setTermos}
                disabled={loading}
              />
            </div>
          )}

          {periodoPreenchido && !ordemPeriodoValida && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {config.msgOrdemPeriodo}
            </Alert>
          )}

          {periodoPreenchido && competenciaFutura && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {MSG_COMPETENCIA_FUTURA}
            </Alert>
          )}

          {mensagensInfo.map((m, i) => (
            <Alert key={`info-${i}`} severity="info" sx={{ mb: 1.5 }}>
              {m.texto}
            </Alert>
          ))}

          {(conflitoBloqueia || mensagensErro.length > 0) &&
            mensagensErro.map((m, i) => (
              <Alert key={`erro-${i}`} severity="error" sx={{ mb: 1.5 }}>
                {m.texto}
              </Alert>
            ))}

          {/* TXM-336 (2.A.7) — CTA do gate impostosEsocial; a mensagem de erro em si já
              vem em mensagensErro (texto genérico calculado no backend). */}
          {validacaoApi?.impostosEsocial?.completo === false && (
            <Alert
              severity="info"
              sx={{ mb: 1.5 }}
              action={
                <Button
                  component="a"
                  href={validacaoApi.impostosEsocial.urlParametrizacao}
                  target="_blank"
                  rel="noreferrer"
                  size="small"
                  color="inherit"
                >
                  Cadastrar impostos
                </Button>
              }
            >
              Cadastre as alíquotas de eSocial para as competências faltantes antes de
              criar a apuração.
            </Alert>
          )}

          {alertaPeriodoMais5Anos && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              O período inicial é anterior a 5 anos. Verifique se deseja continuar.
            </Alert>
          )}

          {validacaoIndisponivel && podeValidarRemoto && !loadingValidacao && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Não foi possível pré-validar automaticamente esta combinação — o sistema
              validará ao confirmar.
            </Alert>
          )}
        </DialogContent>

        <DialogActions className={styles.containerBtn}>
          <Button
            onClick={handleClose}
            variant="contained"
            className={styles.btnCancelar}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmar}
            variant="contained"
            className={styles.btnSalvar}
            disabled={loading || loadingTiposApuracao || !formValido}
          >
            {labelConfirmar}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
};

export default ModalNovaApuracao;
